// TB-184: the one place the portal resolves a consultation (visit) type. Types
// are admin-managed rows (providers-api consultation-types): each carries a
// kind ('clinic' | 'video' | 'home' picks the icon and behaviour), display names
// (EN/AR), a sort order and flags. Code never compares the stored title; the old
// title mapping is only the fallback for a record or a string the registry does
// not know (old rows, an older API, the list not loaded yet).
import { reactive } from 'vue'
import axios from 'axios'

export const KINDS = ['clinic', 'video', 'home']

const DEFAULTS = {
  clinic: { nameEn: 'In clinic', nameAr: 'في العيادة', sortOrder: 10, requiresOnlinePayment: false, allowsCallJoin: false, hasLocation: true },
  video: { nameEn: 'Video call', nameAr: 'مكالمة فيديو', sortOrder: 20, requiresOnlinePayment: true, allowsCallJoin: true, hasLocation: false },
  home: { nameEn: 'Home visit', nameAr: 'زيارة منزلية', sortOrder: 30, requiresOnlinePayment: false, allowsCallJoin: false, hasLocation: true }
}

export const visitTypeRegistry = reactive({ list: [], loaded: false })

const norm = (s) => String(s || '').trim().toLowerCase()

export function kindOfTitle (title) {
  const t = norm(title)
  if (t.includes('video')) return 'video'
  if (t.includes('home')) return 'home'
  return 'clinic'
}

function withDefaults (t) {
  const kind = KINDS.includes(norm(t && t.kind)) ? norm(t.kind) : kindOfTitle(t && t.title)
  const d = DEFAULTS[kind]
  return {
    ...t,
    kind,
    nameEn: (t && t.nameEn) || d.nameEn,
    nameAr: (t && t.nameAr) || d.nameAr,
    isActive: t && t.isActive !== undefined ? t.isActive !== false : !(t && t.status === false),
    sortOrder: t && t.sortOrder !== undefined && t.sortOrder !== null ? Number(t.sortOrder) : d.sortOrder,
    requiresOnlinePayment: t && typeof t.requiresOnlinePayment === 'boolean' ? t.requiresOnlinePayment : d.requiresOnlinePayment,
    allowsCallJoin: t && typeof t.allowsCallJoin === 'boolean' ? t.allowsCallJoin : d.allowsCallJoin,
    hasLocation: t && typeof t.hasLocation === 'boolean' ? t.hasLocation : d.hasLocation
  }
}

export function setVisitTypes (list) {
  visitTypeRegistry.list = (Array.isArray(list) ? list : []).filter((t) => t && t.id).map(withDefaults)
  visitTypeRegistry.loaded = true
}

/** The type record a reference points at: an object, an id, a title, a display name or a kind key. */
export function findVisitType (ref) {
  if (!ref) return null
  const list = visitTypeRegistry.list
  if (typeof ref === 'object') {
    if (ref.id) {
      const hit = list.find((t) => t.id === ref.id)
      if (hit) return hit
    }
    return ref.kind || ref.nameEn || ref.title ? withDefaults(ref) : null
  }
  const s = norm(ref)
  if (!s) return null
  return list.find((t) => t.id === ref) ||
    list.find((t) => norm(t.title) === s) ||
    list.find((t) => norm(t.nameEn) === s || norm(t.nameAr) === s) ||
    null
}

/** clinic | video | home. A bare kind key ('video') resolves to itself. */
export function visitKindOf (ref) {
  if (typeof ref === 'string' && KINDS.includes(norm(ref)) && !findVisitType(ref)) return norm(ref)
  const t = findVisitType(ref)
  if (t) return t.kind
  return kindOfTitle(typeof ref === 'object' && ref ? ref.title : ref)
}

// The first (lowest sortOrder) type of a kind, for the places that only know a kind.
function firstOfKind (kind) {
  return visitTypeRegistry.list
    .filter((t) => t.kind === kind)
    .sort((a, b) => (b.isActive - a.isActive) || (a.sortOrder - b.sortOrder))[0] || null
}

/** Display name of a type reference (record, id, title, name or kind key). */
export function visitTypeLabel (ref, lang = 'en') {
  const t = findVisitType(ref)
  const kind = t ? t.kind : visitKindOf(ref)
  const rec = t || firstOfKind(kind)
  const own = rec && (lang === 'ar' ? rec.nameAr : rec.nameEn)
  return own || DEFAULTS[kind][lang === 'ar' ? 'nameAr' : 'nameEn']
}

/** Display name of a kind, from the first type of that kind. */
export function kindLabel (kind, lang = 'en') {
  const rec = firstOfKind(kind)
  return (rec && (lang === 'ar' ? rec.nameAr : rec.nameEn)) || (DEFAULTS[kind] || DEFAULTS.clinic)[lang === 'ar' ? 'nameAr' : 'nameEn']
}

const flag = (ref, name, kindDefault) => {
  const t = findVisitType(ref)
  if (t && typeof t[name] === 'boolean') return t[name]
  return kindDefault(visitKindOf(ref))
}

/** A doctor-booked visit of this type stays pending until the patient pays. */
export const requiresOnlinePayment = (ref) => flag(ref, 'requiresOnlinePayment', (k) => DEFAULTS[k].requiresOnlinePayment)
/** The visit has a video call to join. */
export const allowsCallJoin = (ref) => flag(ref, 'allowsCallJoin', (k) => DEFAULTS[k].allowsCallJoin)
/** The visit happens at a place (clinic or the patient's address). */
export const hasLocation = (ref) => flag(ref, 'hasLocation', (k) => DEFAULTS[k].hasLocation)

/** Active types, in the admin's order, for pickers and filters. Keeps everything if none is flagged active. */
export function activeVisitTypes (list) {
  const rows = (Array.isArray(list) ? list : []).filter((t) => t && t.id).map(withDefaults)
  const active = rows.filter((t) => t.isActive)
  return (active.length ? active : rows).sort((a, b) => a.sortOrder - b.sortOrder)
}

let pending = null

/** Reads the types once per session (inactive ones too, for old bookings); `force` re-reads. */
export function loadVisitTypes ({ force = false } = {}) {
  if (pending && !force) return pending
  const base = process.env.VUE_APP_PROVIDERS_SERVICE_API || ''
  pending = axios.get(`${base}consultation-types`, { params: { includeInactive: true } })
    .then((r) => setVisitTypes(r && r.data && r.data.data))
    .catch(() => { visitTypeRegistry.loaded = true })
    .then(() => visitTypeRegistry)
  return pending
}
